export interface StartupHealthInput {
  streamLength?: number
  duration?: number
  downloadSpeed?: number
  bufferedSeconds?: number
  peers?: number
  streamProgress?: number
}

export interface StartupHealth {
  status: 'unknown' | 'safe' | 'warning'
  requiredSpeed: number
  downloadSpeed: number
  bufferedSeconds: number
  stallInSeconds?: number
}

export type PlaybackRecoveryCause =
  | 'stall'
  | 'media-error'
  | 'network-error'
  | 'decode-error'
  | 'source-not-supported'
  | 'missing-video'
  | 'degraded-video'
  | 'premature-end'
  | 'seek-timeout'
  | 'startup-timeout'
export type PlaybackRecoveryAction = 'none' | 'wait-network' | 'fallback' | 'rollback' | 'refresh-source' | 'reload' | 'fail'

export interface PlaybackRecoveryInput {
  cause: PlaybackRecoveryCause
  stalledFor?: number
  online?: boolean
  hasAlternate?: boolean
  alternateTried?: boolean
  hasRollback?: boolean
  networkStarved?: boolean
  canRefreshSource?: boolean
  refreshAttempts?: number
  reloadAttempts?: number
}

export interface VideoProgressSample {
  observedAt: number
  mediaTime: number
  totalVideoFrames: number
  droppedVideoFrames?: number
}

export interface VideoFrameCounters {
  totalVideoFrames: number
  droppedVideoFrames: number
}

interface VideoFrameSource {
  getVideoPlaybackQuality?: () => {
    totalVideoFrames?: number
    droppedVideoFrames?: number
  }
  webkitDecodedFrameCount?: number
  webkitDroppedFrameCount?: number
}

interface TimeRangesLike {
  length: number
  start(index: number): number
  end(index: number): number
}

export function parseRuntimeSeconds(runtime = '') {
  const hours = runtime.match(/([\d.,]+)\s*(?:h|hr|hour)/i)
  const minutes = runtime.match(/([\d.,]+)\s*(?:m|min|minute)/i)
  const seconds = runtime.match(/([\d.,]+)\s*(?:s|sec|second)/i)
  if (!hours && !minutes && !seconds) return 0
  const hourValue = hours ? Number.parseFloat(hours[1].replace(',', '.')) : 0
  const minuteValue = minutes ? Number.parseFloat(minutes[1].replace(',', '.')) : 0
  const secondValue = seconds ? Number.parseFloat(seconds[1].replace(',', '.')) : 0
  return Math.round(
    ((Number.isFinite(hourValue) ? hourValue : 0) * 60 + (Number.isFinite(minuteValue) ? minuteValue : 0)) * 60
      + (Number.isFinite(secondValue) ? secondValue : 0),
  )
}

export function stableDownloadSpeed(samples: number[]) {
  const positive = samples.filter((value) => Number.isFinite(value) && value > 0).sort((a, b) => a - b)
  if (!positive.length) return samples.length ? 0 : undefined
  const middle = Math.floor(positive.length / 2)
  return positive.length % 2 ? positive[middle] : (positive[middle - 1] + positive[middle]) / 2
}

/** A small canplay buffer is not enough when torrent speed only barely exceeds
 * the average bitrate. This target absorbs peer and segment jitter without
 * making a healthy, fast source wait unnecessarily. */
export function startupBufferTarget(
  requiredSpeed = 0,
  downloadSpeed = 0,
  transport: 'hls' | 'direct' = 'direct',
) {
  const floor = transport === 'hls' ? 10 : 8
  if (!requiredSpeed || !downloadSpeed) return floor
  const headroom = downloadSpeed / requiredSpeed
  if (headroom < 1.05) return 45
  if (headroom < 1.2) return 35
  if (headroom < 1.4) return 24
  if (headroom < 1.8) return 14
  return floor
}

/** Recovery is deliberately bounded. A bad source must not flap forever
 * between pipelines or reload in a loop while somebody is trying to exit. */
export function playbackRecoveryAction({
  cause,
  stalledFor = 0,
  online = true,
  hasAlternate = false,
  alternateTried = false,
  hasRollback = false,
  networkStarved = false,
  canRefreshSource = false,
  refreshAttempts = 0,
  reloadAttempts = 0,
}: PlaybackRecoveryInput): PlaybackRecoveryAction {
  if (!online) return 'wait-network'
  if ((cause === 'stall' || cause === 'startup-timeout') && networkStarved) {
    return stalledFor < 15_000 ? 'none' : 'fail'
  }
  if (cause === 'stall' && stalledFor < 7_000) return 'none'
  if (hasRollback) return 'rollback'
  // A CDN/media-network error is more likely to be an expired signed link than
  // a decoder incompatibility. Renew it before changing an otherwise healthy
  // transport, while keeping the same one-attempt bound.
  if (cause === 'network-error' && canRefreshSource && refreshAttempts < 1) return 'refresh-source'
  if (hasAlternate && !alternateTried) return 'fallback'
  if (canRefreshSource && refreshAttempts < 1) return 'refresh-source'
  if (reloadAttempts < 1) return 'reload'
  return 'fail'
}

/** Translate the native MediaError code without treating a deliberate abort as
 * a broken source. The numeric constants are stable across older webOS builds. */
export function classifyMediaError(code = 0): PlaybackRecoveryCause | undefined {
  if (code === 1) return undefined
  if (code === 2) return 'network-error'
  if (code === 3) return 'decode-error'
  if (code === 4) return 'source-not-supported'
  return 'media-error'
}

/** Chrome 79-era webOS builds often expose only the prefixed counters. Reading
 * either API is guarded because vendor implementations can throw while the
 * native pipeline is being replaced. */
export function readVideoFrameCounters(source?: VideoFrameSource | null): VideoFrameCounters | undefined {
  if (!source) return undefined
  let standard: VideoFrameCounters | undefined
  try {
    const quality = source.getVideoPlaybackQuality?.()
    const total = quality?.totalVideoFrames
    const dropped = quality?.droppedVideoFrames
    if (typeof total === 'number' && Number.isFinite(total) && total >= 0) {
      standard = {
        totalVideoFrames: total,
        droppedVideoFrames: typeof dropped === 'number' && Number.isFinite(dropped) && dropped >= 0 ? dropped : 0,
      }
      if (total > 0) return standard
    }
  } catch { /* fall through to the older webOS counters */ }

  const decoded = source.webkitDecodedFrameCount
  const dropped = source.webkitDroppedFrameCount
  if (typeof decoded !== 'number' || !Number.isFinite(decoded) || decoded < 0) return standard
  return {
    totalVideoFrames: decoded,
    droppedVideoFrames: typeof dropped === 'number' && Number.isFinite(dropped) && dropped >= 0 ? dropped : 0,
  }
}

/** currentTime can continue on an audio clock while LG's video decoder has
 * frozen. Only act when the runtime exposes real frame counters and a full
 * observation window proves that media time advanced without decoded frames. */
export function frozenVideoOutput(previous: VideoProgressSample, current: VideoProgressSample) {
  const elapsed = current.observedAt - previous.observedAt
  const mediaAdvanced = current.mediaTime - previous.mediaTime
  const frameAdvanced = current.totalVideoFrames - previous.totalVideoFrames
  return elapsed >= 6_000 && mediaAdvanced >= 4 && frameAdvanced >= 0 && frameAdvanced <= 1
}

/** Recover when the TV is technically advancing but cannot render smoothly.
 * Require a full sample window, meaningful playback and severe sustained loss
 * so credits, seeks and an occasional expensive frame never cause a switch. */
export function degradedVideoOutput(previous: VideoProgressSample, current: VideoProgressSample) {
  const elapsed = current.observedAt - previous.observedAt
  const mediaAdvanced = current.mediaTime - previous.mediaTime
  const frameAdvanced = current.totalVideoFrames - previous.totalVideoFrames
  const dropped = (current.droppedVideoFrames || 0) - (previous.droppedVideoFrames || 0)
  return elapsed >= 6_000 && mediaAdvanced >= 4 && frameAdvanced >= 60
    && dropped >= 18 && dropped / frameAdvanced >= 0.18
}

/** TimeRanges can mutate between length/start/end calls on LG's native media
 * pipeline. Return a conservative snapshot instead of letting IndexSizeError
 * escape from a progress event and break the React player. */
export function bufferedMediaState(currentTime = 0, duration = 0, ranges?: TimeRangesLike | null) {
  const current = Number.isFinite(currentTime) ? Math.max(0, currentTime) : 0
  let count = 0
  try { count = Math.max(0, Math.min(100, ranges?.length || 0)) } catch { count = 0 }
  let furthest = 0
  let readyUntil = current
  for (let index = 0; index < count; index += 1) {
    try {
      const start = ranges!.start(index)
      const end = ranges!.end(index)
      if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) continue
      furthest = Math.max(furthest, end)
      if (start <= current + 0.5 && end >= current) readyUntil = Math.max(readyUntil, end)
    } catch { break }
  }
  const finiteDuration = Number.isFinite(duration) && duration > 0 ? duration : 0
  return {
    fraction: finiteDuration ? Math.max(0, Math.min(1, furthest / finiteDuration)) : 0,
    ahead: Math.max(0, readyUntil - current),
  }
}

/** A short background trip can reuse the decoder. A real webOS suspension is
 * safer to resume from an exact-position pipeline rebuild than a stale socket. */
export function shouldRebuildAfterSuspend(hiddenForMs = 0, wasPlaying = false, wantsPlayback = false) {
  return wasPlaying && wantsPlayback && Number.isFinite(hiddenForMs) && hiddenForMs >= 30_000
}

/** Some native HLS pipelines emit ended when a segment chain truncates. Do not
 * mark a title watched or autoplay the next episode while a known duration is
 * still materially ahead of the media clock. */
export function prematureMediaEnd(currentTime = 0, expectedDuration = 0) {
  if (!Number.isFinite(currentTime) || !Number.isFinite(expectedDuration) || expectedDuration <= 10) return false
  return expectedDuration - Math.max(0, currentTime) > 5
}

function epochMilliseconds(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  if (/^\d{9,13}$/.test(trimmed)) {
    const numeric = Number(trimmed)
    if (!Number.isFinite(numeric)) return undefined
    return trimmed.length <= 10 ? numeric * 1_000 : numeric
  }
  if (/^\d+$/.test(trimmed)) return undefined
  const parsed = Date.parse(trimmed)
  return Number.isFinite(parsed) ? parsed : undefined
}

function awsTimestamp(value: string) {
  const match = value.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/)
  if (!match) return undefined
  return Date.UTC(
    Number(match[1]), Number(match[2]) - 1, Number(match[3]),
    Number(match[4]), Number(match[5]), Number(match[6]),
  )
}

/** Read common CDN expiry schemes without exposing or persisting the signed
 * URL. Undefined means the provider did not publish a usable expiry hint. */
export function mediaUrlExpiresAt(value = '') {
  let url: URL
  try { url = new URL(value) } catch { return undefined }
  const params = new Map(Array.from(url.searchParams.entries(), ([key, entry]) => [key.toLowerCase(), entry]))
  const candidates = ['expires', 'expire', 'expiry', 'exp', 'e', 'se']
    .map((key) => params.get(key))
    .filter((entry): entry is string => Boolean(entry))
    .map(epochMilliseconds)
    .filter((entry): entry is number => entry !== undefined && entry >= 946_684_800_000 && entry <= 9_999_999_999_999)

  const awsDate = params.get('x-amz-date')
  const awsLifetime = Number(params.get('x-amz-expires'))
  const awsStart = awsDate ? awsTimestamp(awsDate) : undefined
  if (awsStart !== undefined && Number.isFinite(awsLifetime) && awsLifetime >= 0 && awsLifetime <= 604_800) {
    candidates.push(awsStart + awsLifetime * 1_000)
  }

  const googleDate = params.get('x-goog-date')
  const googleLifetime = Number(params.get('x-goog-expires'))
  const googleStart = googleDate ? awsTimestamp(googleDate) : undefined
  if (googleStart !== undefined && Number.isFinite(googleLifetime) && googleLifetime >= 0 && googleLifetime <= 604_800) {
    candidates.push(googleStart + googleLifetime * 1_000)
  }

  // Akamai tokens keep their public expiry inside a compound query value.
  // Read only that timestamp; never retain or report the token/HMAC.
  ;['hdnts', 'hdnea'].forEach((key) => {
    const compound = params.get(key)
    const expiry = compound?.match(/(?:^|~)exp=(\d{9,13})(?:~|$)/i)?.[1]
    const parsed = expiry ? epochMilliseconds(expiry) : undefined
    if (parsed !== undefined && parsed >= 946_684_800_000 && parsed <= 9_999_999_999_999) candidates.push(parsed)
  })
  return candidates.length ? Math.min(...candidates) : undefined
}

/** Do not reopen an interrupted session in its credits or final few percent.
 * The ordinary progress store already marks this range watched; this closes
 * the lifecycle-session path that could otherwise revive it after a crash. */
/**
 * Where playback should start. The owner can switch resuming off for the whole
 * TV, in which case everything begins at zero however much progress was saved.
 */
export function resumeStartTime(
  saved: { currentTime: number; duration: number } | undefined,
  creditsStart: number | undefined,
  resumeAllowed: boolean,
) {
  if (!saved || !resumeAllowed) return 0
  return validatedResumeTime(saved.currentTime, saved.duration, creditsStart)
}

export function validatedResumeTime(currentTime = 0, duration = 0, creditsStart?: number) {
  if (!Number.isFinite(currentTime) || currentTime < 5) return 0
  if (duration > 0 && Number.isFinite(duration)) {
    const bounded = Math.min(currentTime, duration)
    const validCredits = Number.isFinite(creditsStart) && Number(creditsStart) > duration * 0.5
      && Number(creditsStart) < duration
    if (bounded / duration >= 0.94 || (validCredits && bounded >= Number(creditsStart))) return 0
    return Math.min(bounded, Math.max(0, duration - 1))
  }
  return Math.min(currentTime, 86_400)
}

export function assessStartupHealth({
  streamLength = 0,
  duration = 0,
  downloadSpeed,
  bufferedSeconds = 0,
  peers = 0,
  streamProgress = 0,
}: StartupHealthInput): StartupHealth {
  const base = {
    requiredSpeed: duration > 0 ? streamLength / duration : 0,
    downloadSpeed: Math.max(0, downloadSpeed || 0),
    bufferedSeconds: Math.max(0, bufferedSeconds),
  }
  if (streamProgress >= 0.98) return { status: 'safe', ...base }
  if (!streamLength || !duration || downloadSpeed === undefined) return { status: 'unknown', ...base }

  const speedRatio = base.downloadSpeed / base.requiredSpeed
  if (speedRatio >= 1) return { status: 'safe', ...base }

  const stallInSeconds = base.bufferedSeconds / Math.max(0.01, 1 - speedRatio)
  const cannotSustain = speedRatio < 0.9
  const thinBuffer = stallInSeconds < 180
  const noSource = peers === 0 && base.downloadSpeed === 0
  return {
    status: cannotSustain || thinBuffer || noSource ? 'warning' : 'safe',
    ...base,
    stallInSeconds,
  }
}
